/**
 * SEO utilities for Cosmos Wave
 * Optimized for Amazon SPN agency and creative strategy
 */

// Central domain configuration
export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cosmos-wave.com';
export const absoluteUrl = (path = '') => new URL(path, SITE_URL).toString();

export interface SEOMeta {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string[];
  noindex?: boolean;
  robots?: string;
  type?: 'website' | 'article';
}

export const defaultSEO: SEOMeta = {
  title: "Agence Amazon SPN — Cosmos Wave | Expert Amazon & Creative Strategy",
  description: "Agence Amazon partenaire SPN. Stratégie e-commerce Amazon, optimisation fiches produits, Amazon Ads et créativité de marque pour booster vos ventes.",
  canonical: "https://cosmos-wave.com/",
  ogImage: "/og/default-1200x630.jpg",
  keywords: ["agence Amazon", "Amazon SPN", "Amazon Ads", "partenaire Amazon", "optimisation Amazon", "fiches produits Amazon", "A+ content", "Amazon Store"],
  robots: "index,follow"
};

export const pageSEO = {
  home: {
    title: "Agence Amazon SPN — Cosmos Wave | Expert Amazon & Creative Strategy",
    description: "Agence Amazon partenaire SPN. Stratégie e-commerce Amazon, optimisation fiches produits, Amazon Ads et créativité de marque pour booster vos ventes.",
    keywords: ["agence Amazon", "Amazon SPN", "Amazon Ads", "partenaire Amazon", "optimisation Amazon", "fiches produits Amazon", "A+ content", "Amazon Store"],
    canonical: absoluteUrl("/")
  },
  services: {
    title: "Services Amazon | Agence SPN & SP-API",
    description: "Services complets Amazon : stratégie e-commerce, Amazon Ads (SP/SB/SD), optimisation fiches produits, A+ Content, Amazon Store et créativité de marque.",
    keywords: ["services Amazon", "Amazon Ads", "stratégie Amazon", "optimisation Amazon", "A+ content", "Amazon Store", "SP API", "SPN partner"],
    canonical: absoluteUrl("/services")
  },
  accompagnement: {
    title: "Agence d'accompagnement Amazon | Gestion de compte Amazon A→Z",
    description: "Accompagnement Amazon : audit, gestion de compte, Buy Box, Ads SP/SB/SD/DSP, A+ & Stores, conformité et FBA/FBM. Audit gratuit.",
    keywords: ["accompagnement Amazon", "agence d'accompagnement Amazon", "gestion compte Amazon", "accompagnement Seller Central"],
    canonical: absoluteUrl("/accompagnement-amazon")
  },
  cases: {
    title: "Résultats Clients Amazon — Cosmos Wave | Études de Cas SPN",
    description: "Découvrez nos résultats clients Amazon : +200% de croissance, optimisation Buy Box, campagnes Amazon Ads performantes. Cas réels d'agence SPN.",
    keywords: ["résultats Amazon", "cas clients Amazon", "performance Amazon", "croissance Amazon", "Buy Box", "Amazon Ads résultats"],
    canonical: absoluteUrl("/cases")
  },
  industriels: {
    title: "Amazon pour Industriels & Fabricants — Cosmos Wave SPN",
    description: "Spécialistes Amazon pour industriels et fabricants. Stratégie B2B Amazon, gestion catalogues, expansion européenne et optimisation Digital Shelf.",
    keywords: ["Amazon industriels", "Amazon fabricants", "B2B Amazon", "expansion Amazon Europe", "Digital Shelf industriel"],
    canonical: absoluteUrl("/industriels-fabricants")
  },
  marques: {
    title: "Amazon pour Marques DNVB — Cosmos Wave | Agence SPN Creative",
    description: "Agence Amazon spécialisée marques DNVB. Créativité, branding Amazon, A+ Content premium, Amazon Store design et stratégie omnicanale.",
    keywords: ["Amazon DNVB", "marques Amazon", "branding Amazon", "A+ content créatif", "Amazon Store design", "creative strategy"],
    canonical: absoluteUrl("/marques-dnvb")
  },
  blog: {
    title: "Blog Amazon & E-commerce — Cosmos Wave | Expertise SPN",
    description: "Actualités Amazon, guides e-commerce, études de cas et conseils d'experts SPN pour optimiser vos performances sur Amazon.",
    keywords: ["blog Amazon", "actualités e-commerce", "guides Amazon", "conseils SPN", "expertise Amazon"],
    canonical: absoluteUrl("/blog")
  },
  ia: {
    title: "IA pour Amazon — Cosmos Wave | Solutions Intelligence Artificielle",
    description: "Solutions IA avancées pour Amazon : agents conversationnels, optimisation automatique, prévisions de stock et analyse de performance.",
    keywords: ["IA Amazon", "intelligence artificielle", "agents IA", "automatisation Amazon", "prévisions stock"],
    canonical: absoluteUrl("/ia")
  }
};

export const structuredData = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Cosmos Wave",
    "alternateName": "Cosmos Wave - Agence Amazon SPN",
    "url": SITE_URL,
    "logo": absoluteUrl("/icons/icon-512x512.png"),
    "description": "Agence Amazon partenaire SPN spécialisée dans la stratégie e-commerce Amazon, l'optimisation des fiches produits, Amazon Ads et la créativité de marque.",
    "foundingDate": "2021",
    "areaServed": {
      "@type": "Place",
      "name": "France"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 48.8566,
        "longitude": 2.3522
      },
      "geoRadius": "500000"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "areaServed": "FR",
      "availableLanguage": ["French", "English"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/cosmoswave"
    ]
  },
  website: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Cosmos Wave",
    "url": SITE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  }
};

export function generateBreadcrumbs(path: string) {
  const paths = path.split('/').filter(Boolean);
  const breadcrumbs = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Accueil",
      "item": absoluteUrl("/")
    }
  ];

  let currentPath = SITE_URL;
  paths.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const name = segment === 'services' ? 'Services' :
                 segment === 'cases' ? 'Études de cas' :
                 segment === 'industriels-fabricants' ? 'Industriels & Fabricants' :
                 segment === 'marques-dnvb' ? 'Marques DNVB' :
                 segment === 'blog' ? 'Blog' :
                 segment === 'ia' ? 'IA' :
                 segment.charAt(0).toUpperCase() + segment.slice(1);
    
    breadcrumbs.push({
      "@type": "ListItem",
      "position": index + 2,
      "name": name,
      "item": currentPath
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs
  };
}

// FAQ Schema generator
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

// Article Schema generator
export function generateArticleSchema(article: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author?: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.headline,
    "description": article.description,
    "image": absoluteUrl(article.image),
    "datePublished": article.datePublished,
    "dateModified": article.dateModified,
    "author": {
      "@type": "Organization",
      "name": article.author || "Cosmos Wave",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "logo": {
        "@type": "ImageObject",
        "url": absoluteUrl("/icons/icon-512x512.png")
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": article.url
    }
  };
}
